import { useMemo, useState, type ReactNode } from 'react';
import { CheckboxRow, Pill } from '../points/controls';
import type { SalaryPreviewScope } from './salaryDefinition';
import {
  formatMoney,
  formatPoints,
  formatSalary,
  poolSizeReassurance,
  poolSizeWarning,
  strengthLabel,
  unreachableTopBand,
} from './salaryWording';

/**
 * §9.1's right-hand pane: the pool this model would produce, its size, the shape of its prices,
 * and what is wrong with it.
 *
 * <b>This is what makes the screen a design tool rather than a config form.</b> Nobody can judge
 * five price bands by reading them. What they can judge is a list of their own regulars with
 * prices against their names, and whether the pool is a size that makes a contest worth entering —
 * which §4.5 calls the single most important contest parameter, and which is why the size warning
 * is the loudest thing on the pane rather than a line at the bottom.
 *
 * Three things it must not do, each for a stated reason:
 *
 * - **It never shows a raw percentile** (§9.3). A player's standing is five words, not a figure;
 *   a band is a share of the pool, not a range of ranks.
 * - **It never computes a price.** Every number here came from the same engine that will freeze
 *   the pool when a contest publishes, which is the only reason a preview is worth looking at.
 * - **It never blanks the table while the next answer is worked out.** A 150-row list disappearing
 *   on every keystroke is unreadable exactly while it is being used, so the previous pool stays
 *   with a working marker over it.
 *
 * The counts are reported separately rather than collapsed into one "pool size", because §4.5 is
 * explicit that a single number here is ambiguous in the way that costs an afternoon: qualified is
 * what the specification's own table is quoted in, and what was cut afterwards — by the identity
 * guard, and by the cap — is a different fact.
 */

/**
 * The preview response, declared structurally rather than imported.
 *
 * `@brush/ui` depends on React and nothing else — `Rundown` and `Ticker` set the precedent — so
 * the component takes the shape it needs and the app passes the DTO straight in.
 */
export interface SalaryPoolEntry {
  playerId: string;
  playerName: string;
  displayRank: number;
  salary: number;
  isUnproven: boolean;
  compositePercentile: number | null;
  metrics: {
    cashCount: number;
    leaderboardPoints: number;
    totalEarnings: number;
    eventsEntered: number;
    bestFinish: number | null;
    firstPlaces: number;
  };
}

export interface SalaryPoolResult {
  candidates: number;
  qualified: number;
  unidentifiable: number;
  truncated: number;
  poolSize: number;
  floorSalary: number;
  lookbackLeaderboardIds: string[];
  computedAt: string;
  warnings: string[];
  entries: SalaryPoolEntry[];
}

export type SalaryPreviewState =
  /** The model has problems, so there was nothing worth asking the server about. */
  | { status: 'blocked' }
  /** Nothing to price against yet. */
  | { status: 'idle'; message: string }
  | { status: 'loading'; previous: SalaryPoolResult | null }
  | { status: 'error'; message: string; previous: SalaryPoolResult | null }
  | { status: 'ready'; result: SalaryPoolResult };

/** The same state machine `toPreviewState` runs for the points board, and the same ordering. */
export function toSalaryPreviewState(input: {
  blocked: boolean;
  idleMessage: string | null;
  data: SalaryPoolResult | undefined;
  isFetching: boolean;
  error: string | null;
}): SalaryPreviewState {
  if (input.blocked) return { status: 'blocked' };
  if (input.idleMessage !== null) return { status: 'idle', message: input.idleMessage };
  if (input.error !== null) {
    return { status: 'error', message: input.error, previous: input.data ?? null };
  }
  if (input.isFetching) return { status: 'loading', previous: input.data ?? null };
  if (input.data) return { status: 'ready', result: input.data };
  return { status: 'loading', previous: null };
}

export function SalaryPoolPreviewPane({
  state,
  scope,
  onScopeChange,
  leaderboards,
  topBandFrom,
  readOnly = false,
  header,
}: {
  state: SalaryPreviewState;
  /**
   * §4.1's designer convenience: which boards to price against for this preview only. Stored on
   * the version, never passed to the engine's own lookback, and not frozen by publishing.
   */
  scope: SalaryPreviewScope;
  onScopeChange: (next: SalaryPreviewScope) => void;
  leaderboards: readonly { id: string; name: string; status: string }[];
  /** The bottom edge of the top price band, for §4.2.1's unreachable-tier warning. */
  topBandFrom: number | null;
  /** Whether the caller can persist these settings at all — ownership, not lifecycle. */
  readOnly?: boolean;
  header?: ReactNode;
}) {
  const shown = resultOf(state);
  const stale = state.status === 'loading' || state.status === 'error';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-b border-line px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="font-display text-sm font-semibold text-ink-950">Pool preview</h2>
          {state.status === 'loading' ? (
            <span className="text-[11px] text-ink-800/45">Repricing…</span>
          ) : shown ? (
            <span className="num text-[11px] text-ink-800/40">
              {new Date(shown.computedAt).toLocaleTimeString()}
            </span>
          ) : null}
        </div>
        {/* The same reassurance the points designer's pane gives, and for the same reason: an
            operator who suspects that changing what they are testing against has quietly edited
            what they are building will stop using the preview, and the preview is half of what
            this screen is for. */}
        <p className="mt-0.5 text-[11.5px] text-ink-800/55">
          Your real players, priced by the model on the left. Nothing here is saved onto a contest —
          a contest freezes its own copy when it publishes.
        </p>
        {header ? <div className="mt-3">{header}</div> : null}
      </header>

      <TestLookback
        scope={scope}
        onChange={onScopeChange}
        leaderboards={leaderboards}
        readOnly={readOnly}
      />

      {shown ? <Counts result={shown} /> : null}
      {shown ? <PoolSizeNotice poolSize={shown.poolSize} /> : null}
      {shown ? <Warnings result={shown} topBandFrom={topBandFrom} /> : null}

      <div className={`min-h-0 flex-1 overflow-y-auto ${stale ? 'opacity-60' : ''}`}>
        <Body state={state} shown={shown} />
      </div>
    </div>
  );
}

function Body({
  state,
  shown,
}: {
  state: SalaryPreviewState;
  shown: SalaryPoolResult | null;
}) {
  if (state.status === 'blocked') {
    return (
      <Notice title="Fix the problems on the left">
        The model has something the server would refuse, so there is nothing to price with yet.
        Prices come back as soon as it is valid.
      </Notice>
    );
  }

  if (state.status === 'idle') return <Notice title="Nothing to price yet">{state.message}</Notice>;

  if (state.status === 'error' && !shown) {
    return <Notice title="Couldn’t work out the pool">{state.message}</Notice>;
  }

  if (!shown) return <Notice title="Working it out…">Pricing your players with this model.</Notice>;

  if (shown.poolSize === 0) {
    return (
      <Notice title="Nobody is draftable">
        {shown.candidates === 0
          ? 'No past results were found to price from. Close a series first, or pick a different one in the preview settings above.'
          : `${shown.candidates} players turned up in the series this reads, and none of them clears the minimums in section 5. Loosen them and the pool comes back.`}
      </Notice>
    );
  }

  return (
    <>
      {state.status === 'error' ? (
        <p
          role="alert"
          className="border-b border-line border-l-4 border-l-bad bg-surface px-5 py-2.5 text-[11.5px] text-ink-900"
        >
          {state.message} The pool below is from the last calculation that worked.
        </p>
      ) : null}

      <PriceSpread result={shown} />

      <table className="w-full text-left text-sm">
        <thead className="sticky top-0 bg-surface text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
          <tr className="border-b border-line">
            <th className="w-10 px-5 py-2 text-right font-semibold">#</th>
            <th className="px-2 py-2 font-semibold">Player</th>
            <th className="px-5 py-2 text-right font-semibold">Costs</th>
          </tr>
        </thead>
        <tbody>
          {shown.entries.map((entry) => (
            <tr key={entry.playerId} className="border-b border-line/60 last:border-b-0">
              <td className="num px-5 py-1.5 text-right text-ink-800/60">{entry.displayRank}</td>
              <td className="px-2 py-1.5">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="min-w-0 truncate text-ink-950">{entry.playerName}</span>
                  {/* §4.3: unproven says the model cannot tell this player apart, not that they
                      are cheap. It is true of most of any real pool, which is the point. */}
                  {entry.isUnproven ? (
                    <span
                      title="The model has nothing to tell this player apart from the rest of the floor."
                      className="text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-800/40"
                    >
                      Unproven
                    </span>
                  ) : null}
                </div>
                {/* What the price was built from, in facts rather than in a percentile. */}
                <p className="num mt-0.5 text-[11px] text-ink-800/50">
                  {strengthLabel(entry.compositePercentile)} ·{' '}
                  {entry.metrics.cashCount === 1 ? '1 cash' : `${entry.metrics.cashCount} cashes`}
                  {entry.metrics.leaderboardPoints > 0
                    ? ` · ${formatPoints(entry.metrics.leaderboardPoints)} pts`
                    : ''}
                  {entry.metrics.totalEarnings > 0
                    ? ` · ${formatMoney(entry.metrics.totalEarnings)}`
                    : ''}
                </p>
              </td>
              <td className="num px-5 py-1.5 text-right align-top font-semibold text-ink-950">
                {formatSalary(entry.salary)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* The pool is the whole draftable list; only what is shown here is cut. Saying so stops
          "why does it stop at 250" being read as a pricing bug. */}
      {shown.poolSize > shown.entries.length ? (
        <p className="px-5 py-3 text-[11px] text-ink-800/45">
          Showing the first {shown.entries.length} of {shown.poolSize} draftable players.
        </p>
      ) : null}
    </>
  );
}

/**
 * §4.5's three counts, reported separately.
 *
 * "Report qualified and draftable counts separately rather than choosing one, because a single
 * number here is ambiguous in exactly the way that costs an afternoon." The sentence under them is
 * what turns three numbers into an explanation of where the others went.
 */
function Counts({ result }: { result: SalaryPoolResult }) {
  const cut = result.qualified - result.poolSize;

  return (
    <div className="border-b border-line px-5 py-3">
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <Figure label="Draftable" value={result.poolSize} strong />
        <Figure label="Cleared the minimums" value={result.qualified} />
        <Figure label="Turned up at all" value={result.candidates} />
      </div>
      {cut > 0 ? (
        <p className="mt-1.5 text-[11px] leading-relaxed text-ink-800/50">
          {result.truncated > 0
            ? `The cap in section 5 kept the best ${result.poolSize} and dropped ${result.truncated}.`
            : ''}
          {result.unidentifiable > 0
            ? ` ${result.unidentifiable} qualified but ${
                result.unidentifiable === 1 ? 'is not a player anyone can roster' : 'are not players anyone can roster'
              } — the results name them, but not as a card this room holds.`
            : ''}
        </p>
      ) : null}
    </div>
  );
}

/**
 * §4.5's pool-size rule, and the loudest thing on the pane.
 *
 * <b>It fires at both boundaries and says which way to move</b>, because the two failures are
 * opposite: too small and every team is the same, too large and most entrants score nothing. A
 * pool inside the range is told so rather than left unremarked — silence about the most important
 * setting on the screen reads as the screen not having an opinion.
 */
function PoolSizeNotice({ poolSize }: { poolSize: number }) {
  const warning = poolSizeWarning(poolSize);

  if (warning) {
    return (
      <p className="border-b border-line border-l-4 border-l-warn bg-warn/5 px-5 py-3 text-[11.5px] leading-relaxed text-ink-900">
        {warning}
      </p>
    );
  }

  const good = poolSizeReassurance(poolSize);
  return good ? (
    <p className="border-b border-line px-5 py-2.5 text-[11.5px] leading-relaxed text-ink-800/55">
      {good}
    </p>
  ) : null;
}

function Warnings({
  result,
  topBandFrom,
}: {
  result: SalaryPoolResult;
  topBandFrom: number | null;
}) {
  // §4.2.1's unreachable top band. Computed here rather than in the validator because it depends
  // on the pool the model actually produced — the same band is fine at 150 players and prices
  // nobody at 20.
  const unreachable = unreachableTopBand(topBandFrom, result.poolSize);
  const all = unreachable ? [...result.warnings, unreachable] : result.warnings;

  if (all.length === 0) return null;

  return (
    <ul className="space-y-1 border-b border-line bg-warn/5 px-5 py-3">
      {all.map((warning, i) => (
        <li key={i} className="flex gap-1.5 text-[11.5px] leading-relaxed text-warn">
          <span aria-hidden>!</span>
          <span>{warning}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * How many players sit at each price — §9.1's histogram.
 *
 * The one picture that answers the question a band table cannot: is this a pool with a spread of
 * prices, or is it four hundred players at the floor and eleven above it? §4.3 says most of any
 * real pool belongs at the floor, so a tall first bar is the model working — what this shows is
 * whether there is enough above it for a roster to be a choice.
 */
function PriceSpread({ result }: { result: SalaryPoolResult }) {
  const buckets = useMemo(() => {
    const counts = new Map<number, number>();
    for (const entry of result.entries) {
      counts.set(entry.salary, (counts.get(entry.salary) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0] - b[0]);
  }, [result.entries]);

  if (buckets.length < 2) return null;

  const peak = Math.max(...buckets.map(([, count]) => count));
  const truncated = result.poolSize > result.entries.length;

  return (
    <figure className="border-b border-line px-5 py-3">
      <div
        role="img"
        aria-label={`Players at each price: ${buckets
          .map(([salary, count]) => `${count} at ${formatSalary(salary)}`)
          .join(', ')}.`}
        className="flex h-16 items-end gap-1 border-b border-line"
      >
        {buckets.map(([salary, count]) => (
          <span
            key={salary}
            title={`${count} ${count === 1 ? 'player' : 'players'} at ${formatSalary(salary)}`}
            className="min-w-0 flex-1 bg-felt-700"
            style={{ height: `${Math.max((count / peak) * 100, 3)}%` }}
          />
        ))}
      </div>
      <div className="mt-1 flex gap-1" aria-hidden>
        {buckets.map(([salary]) => (
          <span
            key={salary}
            className="num min-w-0 flex-1 truncate text-center text-[9px] leading-none text-ink-800/40"
          >
            {formatSalary(salary)}
          </span>
        ))}
      </div>
      <figcaption className="mt-1.5 text-[11px] text-ink-800/50">
        How many players sit at each price
        {truncated ? `, over the first ${result.entries.length} shown` : ''}.
      </figcaption>
    </figure>
  );
}

/**
 * §4.1's test harness: price against boards other than the ones the model names.
 *
 * <b>Collapsed by default and labelled *Testing only*</b>, exactly as the points designer's test
 * scope is, and for the identical reason: the screen's subject is the model on the left, and a
 * board picker sitting permanently open above the prices reads as part of what is being built. It
 * is stored on the version and never reaches the engine, so publishing does not freeze it.
 *
 * Empty means "use what the model itself says", which is the ordinary state — the override exists
 * for asking *how would this have priced last summer*, not for configuring the model.
 */
function TestLookback({
  scope,
  onChange,
  leaderboards,
  readOnly,
}: {
  scope: SalaryPreviewScope;
  onChange: (next: SalaryPreviewScope) => void;
  leaderboards: readonly { id: string; name: string; status: string }[];
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);
  const chosen = scope.leaderboardIds;

  return (
    <section className="border-b border-line px-5 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          className="flex items-baseline gap-1.5 text-left"
        >
          <span className="text-[11px] uppercase tracking-[0.08em] text-ink-800/45">
            Price against
          </span>
          {/* Visible collapsed as well as open — by the time somebody expands the section to read
              the sentence below, they have already had the worry. */}
          <Pill>Testing only</Pill>
          <span aria-hidden className="text-[10px] text-ink-800/40">
            {open ? '▾' : '▸'}
          </span>
        </button>

        {!open ? (
          <span className="text-[11.5px] text-ink-800/60">
            {chosen.length === 0
              ? 'What the model itself says'
              : `${chosen.length} chosen`}
          </span>
        ) : null}
      </div>

      {open ? (
        <div className="mt-3 space-y-2">
          {leaderboards.length === 0 ? (
            <p className="text-[11.5px] text-ink-800/55">
              This room has no leaderboards yet, so there is nothing to price from. The pool will be
              empty until a series exists.
            </p>
          ) : (
            <div className="max-h-48 space-y-1.5 overflow-y-auto border border-line bg-ground/40 p-3">
              {leaderboards.map((board) => (
                <CheckboxRow
                  key={board.id}
                  checked={chosen.includes(board.id)}
                  disabled={readOnly}
                  label={board.name}
                  detail={board.status === 'closed' ? undefined : `${board.status} — still moving`}
                  onChange={(on) =>
                    onChange({
                      leaderboardIds: on
                        ? [...chosen, board.id]
                        : chosen.filter((id) => id !== board.id),
                    })
                  }
                />
              ))}
            </div>
          )}
          <p className="border-t border-line pt-2.5 text-[11px] leading-relaxed text-ink-800/55">
            Only used to see what this model would charge while you build it. Tick nothing and it
            uses the series the model itself names, in section 2. Changing this never changes the
            model, and a contest works its own pool out from the model rather than from here.
          </p>
        </div>
      ) : null}
    </section>
  );
}

function Figure({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number;
  strong?: boolean;
}) {
  return (
    <div>
      <div
        className={`num font-figure leading-none ${
          strong ? 'text-lg font-semibold text-ink-950' : 'text-lg text-ink-800/70'
        }`}
      >
        {value.toLocaleString('en-US')}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-ink-800/45">{label}</div>
    </div>
  );
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-5 py-8">
      <p className="text-[13px] font-medium text-ink-950">{title}</p>
      <p className="mt-1.5 max-w-sm text-[12px] leading-relaxed text-ink-800/60">{children}</p>
    </div>
  );
}

function resultOf(state: SalaryPreviewState): SalaryPoolResult | null {
  switch (state.status) {
    case 'ready':
      return state.result;
    case 'loading':
    case 'error':
      return state.previous;
    default:
      return null;
  }
}
